/**
 * StorePicker - Phase 1 of the picking-store/slot split.
 * See PICKING-STORE-AND-SLOT-SPLIT-PLAN.md for the full product context.
 *
 * A NEW, closable modal with three explicit journeys: Browse your local,
 * Click & Collect, or Delivery. Browse resolves a physical store; C&C resolves
 * a collection store; Delivery resolves postcode -> suburb (if needed).
 * Confirming without a slot only sets the BROWSING picking store via
 * LocationManager.persistBrowsingLocation() + postcode/suburb cookies; it never
 * writes reservation cookies (unique_window, delivery_expires_time, activeDelivery).
 * The optional delivery-slot action hands off to the shared CartSlotOverlay.
 * The old drawer keeps owning reservation/checkout until Phase 2's cart-drawer
 * slot overlay ships.
 *
 * Public API: window.StorePicker.open() / window.StorePicker.close()
 *
 * Depends on globals already defined by earlier deferred scripts (load order in
 * layout/theme.liquid): getCookie/setCookie/deliveryURL/storefrontApiURL
 * (liquid-variables.liquid, inline - runs before any deferred script), fetchJSONP
 * (delivery-drawer-functions.js), trapFocus/removeTrapFocus (global.js),
 * window.locationManager (location-manager.js).
 *
 * Store resolution prefers the new Azure storefront API
 * (storefrontApiURL + 'storefront/store-delivery-details') and falls back to the
 * legacy postcodedetails.php JSONP waterfall on any Azure network/CORS/HTTP/empty
 * failure - see INTERNAL-DELIVERY-API-BRIEF.md ("Still open" #5: only
 * staging.harrisfarm.com.au is CORS-allowlisted on the dev API today).
 */
(function () {
  'use strict';

  var SESSION_DISMISS_KEY = 'hf_store_picker_dismissed_session';
  var CHOSEN_KEY = 'hf_picking_store_chosen';
  // Internal catalogue/routing entries, not physical stores a shopper can choose.
  var DIRECT_STORE_EXCLUSIONS = { '11': true, '73': true };
  // Guest Confirm → next signed-in page: write this store to custom.picking_store.
  var PENDING_FAVOURITE_KEY = 'hf_pending_favourite_store';
  // Once-per-session gate for the return-visit "still shopping at" toast.
  var WELCOME_SESSION_KEY = 'hf_store_welcome_shown';
  // Browsing intent only. Never substitute this for the reservation-owned
  // delivery_type cookie/cart attribute.
  var SLOT_MODE_INTENT_KEY = 'hf_slot_mode_intent';
  // Short-lived marker set when the optional native Shopify account control
  // is opened from this modal. Authentication refreshes the storefront; this
  // marker lets init() distinguish that return from an ordinary page load.
  var LOGIN_RESUME_KEY = 'hf_store_picker_login_resume';
  var LOGIN_RESUME_TTL_MS = 30 * 60 * 1000;
  var POSTCODE_REQUEST_TIMEOUT_MS = 10000;

  var modal, panel, titleEl, subtitleEl, form, storeSelect, postcodeInput, findBtn, suburbWrapper, suburbSelect,
    intentButtons, storeWrapper, storeLabel, postcodeWrapper, actionsEl,
    errorEl, selectSlotBtn, confirmBtn,
    findFlowEl, currentPanel, currentNameEl, currentMetaEl, keepBtn, changeBtn,
    welcomeToast, welcomeNameEl, welcomeChangeBtn, welcomeDismissBtn, loginCard,
    storeCombobox, storeTrigger, storeValueEl, storePopup, storeSearch, storeOptionsEl, storeEmptyEl, storeBackBtn,
    suburbChoices;
  var welcomeHideTimer = null;
  var headerCountdownTimer = null;
  var slotExpiredPrompt = false;
  var browseSubmitPending = false;
  var browseSubmitEpoch = 0;
  var cancelBrowseRelease = null;
  var slotPickerOpenPending = false;
  var storePickerOpener = null;
  // Cart-page store changes are checkout decisions, not browsing decisions.
  // The current reservation stays authoritative until a replacement slot has
  // been reserved and the cart has completed its store reconciliation.
  var openOptions = {};

  // Rows for the postcode currently being confirmed (normalised to
  // { picking_store, suburb, ... } either from Azure's catchment shape or
  // straight from postcodedetails.php - see normalizeAzureCatchments()), and
  // the row the shopper has actually selected (either the only row, or the
  // one matching the chosen suburb).
  var lookupRows = null;
  var selectedRow = null;
  var lookupPostcode = null;
  var postcodeRequestGeneration = 0;
  var currentIntent = null; // 'browse' | 'M' | 'EX'
  var clickCollectStores = null;
  var clickCollectStoresRequest = null;
  var storeComboboxActiveIndex = -1;
  var storeComboboxPreviousTitle = '';
  var basketChangeNotice = '';

  /**
   * True only if the shopper has EXPLICITLY chosen a picking store (via this
   * modal or a live reservation) - see location-manager.js hasExplicitPickingStore()
   * for why this can't just check the picking_store cookie/localStorage (both get
   * defaulted to store 76 for every anonymous visitor in first-loaded-scripts.liquid).
   */
  function hasExplicitPickingStore() {
    if (typeof window.hasPickingStore === 'function') return window.hasPickingStore();
    try {
      return localStorage.getItem(CHOSEN_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function setNoPickingStoreClass(isMissing) {
    document.body.classList.toggle('hf-no-picking-store', !!isMissing);
  }

  function cacheElements() {
    modal = document.getElementById('store-picker-modal');
    if (!modal) return false;
    panel = modal.querySelector('.store-picker__panel');
    titleEl = document.getElementById('store-picker-title');
    subtitleEl = document.getElementById('store-picker-subtitle');
    form = document.getElementById('store-picker-form');
    intentButtons = modal.querySelectorAll('[data-store-picker-intent]');
    storeWrapper = document.getElementById('store-picker-store-wrapper');
    storeLabel = document.getElementById('store-picker-store-label');
    storeSelect = document.getElementById('store-picker-store');
    storeCombobox = document.getElementById('store-picker-store-combobox');
    storeTrigger = document.getElementById('store-picker-store-trigger');
    storeValueEl = document.getElementById('store-picker-store-value');
    storePopup = document.getElementById('store-picker-store-popup');
    storeSearch = document.getElementById('store-picker-store-search');
    storeOptionsEl = document.getElementById('store-picker-store-options');
    storeEmptyEl = document.getElementById('store-picker-store-empty');
    storeBackBtn = document.getElementById('store-picker-store-back');
    postcodeWrapper = document.getElementById('store-picker-postcode-wrapper');
    postcodeInput = document.getElementById('store-picker-postcode');
    findBtn = document.getElementById('store-picker-find');
    suburbWrapper = document.getElementById('store-picker-suburb-wrapper');
    suburbSelect = document.getElementById('store-picker-suburb');
    suburbChoices = document.getElementById('store-picker-suburb-choices');
    errorEl = document.getElementById('store-picker-error');
    actionsEl = document.getElementById('store-picker-actions');
    selectSlotBtn = document.getElementById('store-picker-select-slot');
    confirmBtn = document.getElementById('store-picker-confirm');
    findFlowEl = document.getElementById('store-picker-find-flow');
    currentPanel = document.getElementById('store-picker-current');
    currentNameEl = document.getElementById('store-picker-current-name');
    currentMetaEl = document.getElementById('store-picker-current-meta');
    keepBtn = document.getElementById('store-picker-keep');
    changeBtn = document.getElementById('store-picker-change');
    welcomeToast = document.getElementById('store-welcome-toast');
    welcomeNameEl = document.getElementById('store-welcome-name');
    welcomeChangeBtn = document.getElementById('store-welcome-change');
    welcomeDismissBtn = document.getElementById('store-welcome-dismiss');
    loginCard = document.getElementById('store-picker-login-card');
    return true;
  }

  function markLoginRefreshResume() {
    var cid = window.siteWideSettings && window.siteWideSettings.cid;
    if (looksLikeCustomerId(cid)) return;

    // Snapshot the shopper's current choice before Shopify leaves the page
    // for authentication. The signed-in return must not rely solely on the
    // explicit-store flag still being available after that navigation.
    // A postcode is accepted as a legacy explicit-choice signal; unlike the
    // silent store-76 browsing default it only exists after a real delivery
    // location has been entered.
    var activeStore = currentBrowsingStore();
    var activePostcode = currentPostcode();
    var hasActiveChoice = hasExplicitPickingStore() || /^\d{4}$/.test(activePostcode);
    if (looksLikeStoreNumber(activeStore)
      && !DIRECT_STORE_EXCLUSIONS[String(activeStore).trim()]
      && hasActiveChoice) {
      setPendingFavouriteStore(String(activeStore).trim());
    }

    try {
      sessionStorage.setItem(LOGIN_RESUME_KEY, JSON.stringify({
        timestamp: Date.now(),
        path: window.location.pathname + window.location.search
      }));
    } catch (e) {
      console.warn('StorePicker: could not persist login refresh marker:', e);
    }
  }

  function hasFreshLoginRefreshResume() {
    var cid = window.siteWideSettings && window.siteWideSettings.cid;
    if (!looksLikeCustomerId(cid)) return false;
    try {
      var raw = sessionStorage.getItem(LOGIN_RESUME_KEY);
      if (!raw) return false;
      var state = JSON.parse(raw);
      var isFresh = state && Number(state.timestamp) > 0
        && Date.now() - Number(state.timestamp) <= LOGIN_RESUME_TTL_MS;
      if (!isFresh) sessionStorage.removeItem(LOGIN_RESUME_KEY);
      return !!isFresh;
    } catch (e) {
      try { sessionStorage.removeItem(LOGIN_RESUME_KEY); } catch (ignore) { /* ignore */ }
      return false;
    }
  }

  function clearLoginRefreshResume() {
    try { sessionStorage.removeItem(LOGIN_RESUME_KEY); } catch (e) { /* ignore */ }
  }

  function showError(message) {
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.hidden = false;
  }

  function clearError() {
    if (!errorEl) return;
    errorEl.hidden = true;
    errorEl.textContent = '';
  }

  function storeComboboxOptions(visibleOnly) {
    if (!storeOptionsEl) return [];
    return Array.prototype.slice.call(
      storeOptionsEl.querySelectorAll('.store-picker__combobox-option')
    ).filter(function (button) {
      return !visibleOnly || !button.hidden;
    });
  }

  function syncStoreComboboxValue() {
    if (!storeSelect || !storeTrigger || !storeValueEl) return;
    var selectedOption = storeSelect.options[storeSelect.selectedIndex];
    var placeholder = storeSelect.options[0] ? storeSelect.options[0].textContent : 'Choose a store';
    storeValueEl.textContent = selectedOption && selectedOption.value
      ? selectedOption.textContent
      : placeholder;
    storeTrigger.disabled = !!storeSelect.disabled;

    storeComboboxOptions(false).forEach(function (button) {
      button.setAttribute('aria-selected', String(button.dataset.value === storeSelect.value));
    });
  }

  function setStoreComboboxActive(index) {
    var options = storeComboboxOptions(true);
    storeComboboxOptions(false).forEach(function (button) {
      button.classList.remove('is-active');
    });

    if (!options.length) {
      storeComboboxActiveIndex = -1;
      if (storeSearch) storeSearch.removeAttribute('aria-activedescendant');
      return;
    }

    if (index < 0) index = options.length - 1;
    if (index >= options.length) index = 0;
    storeComboboxActiveIndex = index;
    options[index].classList.add('is-active');
    if (storeSearch) storeSearch.setAttribute('aria-activedescendant', options[index].id);
    options[index].scrollIntoView({ block: 'nearest' });
  }

  function filterStoreComboboxOptions() {
    var query = storeSearch ? storeSearch.value.trim().toLowerCase() : '';
    var visibleCount = 0;

    storeComboboxOptions(false).forEach(function (button) {
      var matches = !query || button.dataset.search.indexOf(query) !== -1;
      button.hidden = !matches;
      if (matches) visibleCount += 1;
    });

    if (storeEmptyEl) storeEmptyEl.hidden = visibleCount !== 0;
    var visibleOptions = storeComboboxOptions(true);
    var selectedIndex = visibleOptions.findIndex(function (button) {
      return button.dataset.value === storeSelect.value;
    });
    setStoreComboboxActive(selectedIndex >= 0 ? selectedIndex : 0);
  }

  function closeStoreCombobox(restoreFocus) {
    if (!storePopup || !storeTrigger) return;
    storePopup.hidden = true;
    storeTrigger.setAttribute('aria-expanded', 'false');
    if (panel) panel.classList.remove('store-picker__panel--store-list');
    if (storeBackBtn) storeBackBtn.hidden = true;
    if (titleEl && storeComboboxPreviousTitle) titleEl.textContent = storeComboboxPreviousTitle;
    storeComboboxPreviousTitle = '';
    storeComboboxActiveIndex = -1;
    if (storeSearch) {
      storeSearch.value = '';
      storeSearch.removeAttribute('aria-activedescendant');
    }
    if (restoreFocus) storeTrigger.focus();
  }

  function openStoreCombobox() {
    if (!storePopup || !storeTrigger || storeTrigger.disabled) return;
    storeComboboxPreviousTitle = titleEl ? titleEl.textContent : '';
    if (titleEl) {
      titleEl.textContent = currentIntent === 'M'
        ? 'Choose a collection store'
        : 'Choose your local store';
    }
    if (panel) panel.classList.add('store-picker__panel--store-list');
    if (storeBackBtn) storeBackBtn.hidden = false;
    var body = modal && modal.querySelector('.store-picker__body');
    if (body) body.scrollTop = 0;
    storePopup.hidden = false;
    storeTrigger.setAttribute('aria-expanded', 'true');
    if (storeSearch) storeSearch.value = '';
    filterStoreComboboxOptions();
    window.requestAnimationFrame(function () {
      if (storeSearch) storeSearch.focus();
    });
  }

  function chooseStoreComboboxValue(value) {
    if (!storeSelect) return;
    storeSelect.value = value;
    syncStoreComboboxValue();
    storeSelect.dispatchEvent(new Event('change', { bubbles: true }));
    closeStoreCombobox(true);
  }

  function refreshStoreCombobox() {
    if (!storeSelect || !storeCombobox || !storeTrigger || !storeOptionsEl) return;
    storeOptionsEl.innerHTML = '';

    Array.prototype.slice.call(storeSelect.options).forEach(function (option, index) {
      if (!option.value) return;
      var button = document.createElement('button');
      button.type = 'button';
      button.id = 'store-picker-store-option-' + index;
      button.className = 'store-picker__combobox-option';
      button.setAttribute('role', 'option');
      button.setAttribute('aria-selected', String(option.value === storeSelect.value));
      button.dataset.value = option.value;
      button.dataset.search = option.textContent.trim().toLowerCase();
      button.textContent = option.textContent;
      button.addEventListener('click', function () {
        chooseStoreComboboxValue(button.dataset.value);
      });
      button.addEventListener('mousemove', function () {
        var visibleOptions = storeComboboxOptions(true);
        setStoreComboboxActive(visibleOptions.indexOf(button));
      });
      storeOptionsEl.appendChild(button);
    });

    storeSelect.hidden = true;
    storeCombobox.hidden = false;
    if (storeLabel) storeLabel.removeAttribute('for');
    syncStoreComboboxValue();
    closeStoreCombobox(false);
  }

  function syncSuburbChoices() {
    if (!suburbSelect || !suburbChoices) return;
    Array.prototype.slice.call(
      suburbChoices.querySelectorAll('.store-picker__suburb-choice')
    ).forEach(function (button) {
      button.setAttribute('aria-checked', String(button.dataset.value === suburbSelect.value));
    });
  }

  function renderSuburbChoices() {
    if (!suburbSelect || !suburbChoices) return;
    suburbChoices.innerHTML = '';

    Array.prototype.slice.call(suburbSelect.options).forEach(function (option) {
      if (!option.value) return;
      var button = document.createElement('button');
      var label = document.createElement('span');
      var check = document.createElement('span');
      button.type = 'button';
      button.className = 'store-picker__suburb-choice';
      button.setAttribute('role', 'radio');
      button.setAttribute('aria-checked', String(option.value === suburbSelect.value));
      button.dataset.value = option.value;
      label.textContent = option.textContent;
      check.className = 'store-picker__suburb-choice-check';
      check.setAttribute('aria-hidden', 'true');
      check.textContent = '\u2713';
      button.appendChild(label);
      button.appendChild(check);
      button.addEventListener('click', function () {
        suburbSelect.value = button.dataset.value;
        suburbSelect.dispatchEvent(new Event('change', { bubbles: true }));
      });
      button.addEventListener('keydown', function (event) {
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowRight'
          && event.key !== 'ArrowUp' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        var choices = Array.prototype.slice.call(
          suburbChoices.querySelectorAll('.store-picker__suburb-choice')
        );
        var direction = event.key === 'ArrowDown' || event.key === 'ArrowRight' ? 1 : -1;
        var nextIndex = (choices.indexOf(button) + direction + choices.length) % choices.length;
        choices[nextIndex].focus();
      });
      suburbChoices.appendChild(button);
    });

    suburbSelect.hidden = true;
    suburbChoices.hidden = !suburbChoices.children.length;
    var suburbLabel = document.getElementById('store-picker-suburb-label');
    if (suburbLabel) suburbLabel.removeAttribute('for');
    syncSuburbChoices();
  }

  function enhanceCustomSelectors() {
    if (suburbSelect && suburbChoices) {
      suburbSelect.hidden = true;
      suburbChoices.hidden = true;
    }

    if (!storeCombobox || !storeTrigger || !storePopup || !storeSearch || !storeOptionsEl) return;

    if (storeBackBtn) {
      storeBackBtn.addEventListener('click', function (event) {
        event.preventDefault();
        closeStoreCombobox(true);
      });
    }

    storeTrigger.addEventListener('click', function () {
      if (storePopup.hidden) openStoreCombobox();
      else closeStoreCombobox(false);
    });

    storeTrigger.addEventListener('keydown', function (event) {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp'
        && event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      openStoreCombobox();
      if (event.key === 'ArrowUp') {
        window.requestAnimationFrame(function () {
          setStoreComboboxActive(storeComboboxOptions(true).length - 1);
        });
      }
    });

    storeSearch.addEventListener('input', filterStoreComboboxOptions);
    storeSearch.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        closeStoreCombobox(true);
        return;
      }
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        setStoreComboboxActive(storeComboboxActiveIndex + (event.key === 'ArrowDown' ? 1 : -1));
        return;
      }
      if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        setStoreComboboxActive(event.key === 'Home' ? 0 : storeComboboxOptions(true).length - 1);
        return;
      }
      if (event.key === 'Enter' && storeComboboxActiveIndex >= 0) {
        event.preventDefault();
        var options = storeComboboxOptions(true);
        if (options[storeComboboxActiveIndex]) {
          chooseStoreComboboxValue(options[storeComboboxActiveIndex].dataset.value);
        }
      }
    });

    storeCombobox.addEventListener('focusout', function () {
      window.setTimeout(function () {
        if (!storeCombobox.contains(document.activeElement)) closeStoreCombobox(false);
      }, 0);
    });

    document.addEventListener('click', function (event) {
      if (!storeCombobox.contains(event.target)) closeStoreCombobox(false);
    });
  }

  function setPostcodeLookupPending(pending) {
    if (!findBtn) return;
    findBtn.disabled = pending;
    findBtn.textContent = pending ? 'Checking\u2026' : 'Check delivery';
    if (pending) findBtn.setAttribute('aria-busy', 'true');
    else findBtn.removeAttribute('aria-busy');
  }

  function invalidatePostcodeRequest() {
    postcodeRequestGeneration += 1;
    setPostcodeLookupPending(false);
  }

  function isCurrentPostcodeRequest(generation, postcode) {
    return generation === postcodeRequestGeneration
      && modal && !modal.hidden
      && currentIntent === 'EX'
      && lookupPostcode === postcode
      && postcodeInput && postcodeInput.value.trim() === postcode;
  }

  function resetLookupState() {
    invalidatePostcodeRequest();
    lookupRows = null;
    selectedRow = null;
    clearBasketChangeNotice();
    if (suburbWrapper) suburbWrapper.hidden = true;
    if (suburbSelect) suburbSelect.innerHTML = '<option value="">Select your suburb</option>';
    if (suburbChoices) {
      suburbChoices.innerHTML = '';
      suburbChoices.hidden = true;
    }
    if (selectSlotBtn) selectSlotBtn.hidden = true;
    if (confirmBtn) confirmBtn.hidden = true;
    if (actionsEl) {
      actionsEl.hidden = true;
      actionsEl.classList.remove('store-picker__actions--single');
    }
  }

  function requiresSlotForCurrentOpen() {
    return !!openOptions.requireSlot;
  }

  function requiresFulfilmentChoiceForCurrentOpen() {
    return !!openOptions.requireFulfilmentChoice;
  }

  function hasFulfilmentChoice() {
    if (browseSubmitPending || !hasExplicitPickingStore()) return false;
    // A stale delivery_type cookie alone is not proof of a destination choice.
    // Existing bookings carry a window; slot-free choices carry our saved intent.
    var mode = typeof getCookie === 'function' && getCookie('delivery_unique_window')
      ? getCookie('delivery_type')
      : null;
    if (mode !== 'EX' && mode !== 'M') {
      try { mode = localStorage.getItem(SLOT_MODE_INTENT_KEY); } catch (e) { mode = null; }
    }
    return (mode === 'EX' || mode === 'M')
      && looksLikeStoreNumber(currentBrowsingStore())
      && /^\d{4}$/.test(String(currentPostcode()).trim());
  }

  function storeDisplayName(storeNumber) {
    var lm = window.locationManager;
    if (lm && lm.locations && lm.locations[storeNumber]) {
      return lm.locations[storeNumber].name;
    }
    // storeMatrix is defined in first-loaded-scripts.liquid (same map the old
    // delivery header uses). Fall back to it when LocationManager isn't ready.
    try {
      if (typeof storeMatrix !== 'undefined' && storeMatrix[0]) {
        var fromMatrix = storeMatrix[0][parseInt(storeNumber, 10)];
        if (fromMatrix) return fromMatrix;
      }
    } catch (e) { /* ignore */ }
    return 'Store ' + storeNumber;
  }

  function availableDirectStores() {
    var stores = [];
    var locations = window.locationManager && window.locationManager.locations;

    if (locations) {
      Object.keys(locations).forEach(function (storeNumber) {
        var key = String(storeNumber);
        if (DIRECT_STORE_EXCLUSIONS[key]) return;
        stores.push({ id: key, name: locations[storeNumber].name });
      });
    } else {
      try {
        if (typeof storeMatrix !== 'undefined' && storeMatrix[0]) {
          Object.keys(storeMatrix[0]).forEach(function (storeNumber) {
            var key = String(storeNumber);
            if (DIRECT_STORE_EXCLUSIONS[key]) return;
            stores.push({ id: key, name: storeMatrix[0][storeNumber] });
          });
        }
      } catch (e) { /* ignore */ }
    }

    return stores.sort(function (a, b) {
      return a.name.localeCompare(b.name, 'en-AU');
    });
  }

  function populateDirectStoreSelect() {
    if (!storeSelect) return;
    storeSelect.disabled = false;
    storeSelect.innerHTML = '<option value="">Choose your local store</option>';
    availableDirectStores().forEach(function (store) {
      var option = document.createElement('option');
      option.value = store.id;
      option.textContent = store.name;
      storeSelect.appendChild(option);
    });
    refreshStoreCombobox();
  }

  function setSlotModeIntent(mode) {
    try {
      if (mode === 'EX' || mode === 'M') {
        localStorage.setItem(SLOT_MODE_INTENT_KEY, mode);
      } else {
        localStorage.removeItem(SLOT_MODE_INTENT_KEY);
      }
    } catch (e) {
      console.warn('StorePicker: could not persist slot mode intent:', e);
    }
  }

  function currentSlotModeIntent() {
    var cookieMode = typeof getCookie === 'function' ? getCookie('delivery_type') : '';
    if (cookieMode === 'EX' || cookieMode === 'M') return cookieMode;
    try {
      var storedMode = localStorage.getItem(SLOT_MODE_INTENT_KEY);
      return storedMode === 'EX' || storedMode === 'M' ? storedMode : null;
    } catch (e) {
      return null;
    }
  }

  function normaliseClickCollectStores(response) {
    var stores = response && Array.isArray(response.stores) ? response.stores : [];
    return stores.map(function (store) {
      return {
        id: String(store.store_no || ''),
        pickingStore: String(store.picking_store || store.store_no || ''),
        name: String(store.name || ''),
        // C&C slots and order attributes use the synthetic routing postcode
        // (e.g. Cammeray 9952), not the store's street postcode (2062).
        // The legacy drawer already uses zone_postcode for this exact reason.
        postcode: String(store.zone_postcode || store.postcode || ''),
        physicalPostcode: String(store.postcode || ''),
        city: String(store.city || '')
      };
    }).filter(function (store) {
      return /^\d+$/.test(store.id) && /^\d{4}$/.test(store.postcode) && store.name;
    }).sort(function (a, b) {
      return a.name.localeCompare(b.name, 'en-AU');
    });
  }

  function renderClickCollectStores(stores) {
    if (!storeSelect || currentIntent !== 'M') return;
    storeSelect.disabled = false;
    storeSelect.innerHTML = '<option value="">Choose your collection store</option>';
    stores.forEach(function (store) {
      var option = document.createElement('option');
      option.value = store.id;
      option.textContent = store.name;
      option.dataset.postcode = store.postcode;
      option.dataset.city = store.city;
      option.dataset.pickingStore = store.pickingStore;
      storeSelect.appendChild(option);
    });
    refreshStoreCombobox();
  }

  function loadClickCollectStores() {
    if (clickCollectStores) {
      renderClickCollectStores(clickCollectStores);
      return Promise.resolve(clickCollectStores);
    }
    clearError();
    if (storeSelect) {
      storeSelect.disabled = true;
      storeSelect.innerHTML = '<option value="">Loading collection stores…</option>';
      refreshStoreCombobox();
    }

    // Returning from another journey must restore the loading view even when
    // the same lookup is still running. Reuse that request rather than starting
    // another, or showing Browse stores as collection choices.
    if (clickCollectStoresRequest) return clickCollectStoresRequest;

    var timeoutId;
    var timeout = new Promise(function (resolve, reject) {
      timeoutId = window.setTimeout(function () {
        reject(new Error('collection-store lookup timed out'));
      }, POSTCODE_REQUEST_TIMEOUT_MS * 2);
    });
    var legacyUrl = deliveryURL + 'clickandcollect-stores.php?dummy=1';
    var lookup = Promise.resolve().then(function () {
      return window.StorefrontApi
        ? window.StorefrontApi.getOrLegacy('storefront/click-and-collect-stores', null, legacyUrl)
        : fetchJSONP(legacyUrl, 'c');
    });
    clickCollectStoresRequest = Promise.race([lookup, timeout])
      .then(function (response) {
        var stores = normaliseClickCollectStores(response);
        if (!stores.length) throw new Error('no collection stores returned');
        clickCollectStores = stores;
        renderClickCollectStores(clickCollectStores);
        return clickCollectStores;
      })
      .catch(function (error) {
        console.error('StorePicker: collection-store lookup failed:', error);
        if (currentIntent === 'M') {
          if (storeSelect) {
            storeSelect.disabled = true;
            storeSelect.innerHTML = '<option value="">Collection stores unavailable</option>';
            refreshStoreCombobox();
          }
          showError("We couldn't load collection stores. Please retry or close this window and try later.");
          if (errorEl) {
            var retry = document.createElement('button');
            retry.type = 'button';
            retry.className = 'store-picker__button store-picker__button--secondary';
            retry.textContent = 'Retry loading stores';
            retry.addEventListener('click', function () {
              loadClickCollectStores().catch(function () { /* shown above */ });
            });
            errorEl.appendChild(retry);
          }
        }
        clickCollectStoresRequest = null;
        throw error;
      }).finally(function () {
        window.clearTimeout(timeoutId);
      });

    return clickCollectStoresRequest;
  }

  /**
   * Once-per-session gate for the return-visit toast.
   */
  function markWelcomeShownThisSession() {
    try {
      sessionStorage.setItem(WELCOME_SESSION_KEY, '1');
    } catch (e) { /* ignore */ }
  }

  function wasWelcomeShownThisSession() {
    try {
      return sessionStorage.getItem(WELCOME_SESSION_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function hideWelcomeToast() {
    if (welcomeHideTimer) {
      clearTimeout(welcomeHideTimer);
      welcomeHideTimer = null;
    }
    if (welcomeToast) welcomeToast.hidden = true;
  }

  /**
   * Return visit: device already has an explicit picking store from a prior
   * session. Soft remind once per browser session — bottom-left toast, not a
   * blocking modal. Skipped if StorePicker itself is open or they already saw
   * / dismissed it this session (including after Confirm this visit).
   */
  function maybeShowWelcomeBackToast() {
    if (!welcomeToast || !welcomeNameEl) return;
    if (!hasExplicitPickingStore()) return;
    if (wasWelcomeShownThisSession()) return;
    if (modal && !modal.hidden) return;

    var store = currentBrowsingStore();
    if (!store) return;

    welcomeNameEl.textContent = storeDisplayName(store);
    welcomeToast.hidden = false;
    markWelcomeShownThisSession();

    welcomeHideTimer = setTimeout(hideWelcomeToast, 8000);
  }

  function openWelcomeChangeStore() {
    hideWelcomeToast();
    open();
    // open() may lead with current-store view; jump straight to change form.
    if (hasExplicitPickingStore()) {
      showFindView({ changing: true });
    }
  }

  function reservationIsLive() {
    if (typeof getCookie !== 'function') return false;
    var uniqueWindow = getCookie('delivery_unique_window');
    if (!uniqueWindow) return false;
    var expiresAt = parseInt(getCookie('delivery_expires_time'), 10);
    if (expiresAt && expiresAt <= Date.now()) {
      slotExpiredPrompt = true;
      return false;
    }
    return true;
  }

  function headerReservationCountdown() {
    if (!reservationIsLive() || typeof getCookie !== 'function') return '';
    var expiresAt = parseInt(getCookie('delivery_expires_time'), 10);
    if (!expiresAt) return '';

    var remainingMs = expiresAt - Date.now();
    if (remainingMs <= 0 || remainingMs > 90 * 60 * 1000) return '';

    var minutes = Math.max(1, Math.ceil(remainingMs / 60000));
    return minutes <= 1 ? 'Checkout now to keep this slot' : ('Checkout in ' + minutes + ' min');
  }

  /**
   * A confirmed Browse-your-local choice has no fulfilment commitment. Remove
   * the old delivery/C&C hold everywhere it can survive while preserving the
   * newly selected browsing store plus its postcode/suburb cookies.
   *
   * The Browse submit awaits CartSlotOverlay.releaseForBrowse before this
   * local cleanup. Opening a journey or merely selecting a store never clears
   * a reservation; this helper cannot issue a server-side DELETE itself.
   */
  function clearFulfilmentReservationForBrowse(options) {
    options = options || {};
    if (!options.skipReservationRelease && browseNeedsReservationResolution()) {
      return Promise.reject(new Error('Shared reservation cancellation must finish before Browse can clear fulfilment'));
    }
    var hadReservationState = false;
    var reservationCookies = [
      'delivery_expires_time',
      'delivery_date',
      'delivery_type',
      'delivery_type_long',
      'delivery_unique_window',
      'delivery_time',
      'delivery_time_short',
      'delivery_cc_store',
      'delivery_cc_name',
      'delivery_courier',
      'delivery_run',
      'delivery_type_changed',
      'delivery_later'
    ];

    reservationCookies.forEach(function (name) {
      if (typeof getCookie === 'function' && getCookie(name)) hadReservationState = true;
      if (typeof removeCookie === 'function') {
        removeCookie(name);
      } else {
        document.cookie = name + '=; path=/; max-age=0';
      }
    });

    try {
      if (localStorage.getItem('activeDelivery') === 'true'
        || localStorage.getItem('clickCollectSelected') === 'true') {
        hadReservationState = true;
      }
      localStorage.removeItem('activeDelivery');
      localStorage.removeItem('clickCollectSelected');
    } catch (e) { /* ignore */ }
    document.body.classList.remove('click-collect-active');
    setSlotModeIntent(null);
    slotExpiredPrompt = false;

    // Cancellation is owned by CartSlotOverlay.releaseForBrowse. This function
    // only clears confirmed local routing and must never issue another DELETE.
    if (!hadReservationState && !options.forceCartSync) return Promise.resolve();

    // Reservation-owned order attributes must not ride into checkout after a
    // Browse switch. Keep the newly confirmed browsing picking store as the
    // cart's routing anchor; all slot-owned attributes are cleared before cart
    // lines are reconciled.
    var clearedAttributes = {
      delivery_date: '',
      delivery_run: '',
      picking_store: currentBrowsingStore(),
      courier: '',
      unique_window: '',
      delivery_type: '',
      delivery_cc_store: '',
      postcode: '',
      delivery_suburb: '',
      local_delivery_request: ''
    };
    var attributeSync;
    if (typeof syncDeliveryAttributesToCart === 'function') {
      // With the reservation cookies removed, the shared retrying writer sends
      // the browsing picking store plus empty slot-owned fulfilment attributes.
      attributeSync = syncDeliveryAttributesToCart();
    } else {
      attributeSync = fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ attributes: clearedAttributes })
      }).then(function (response) {
        if (!response.ok) throw new Error('cart attribute clear responded ' + response.status);
        return response.json();
      });
    }

    document.dispatchEvent(new CustomEvent('deliveryReservationExpired', {
      detail: { reason: options.reason || 'browse-local', store: currentBrowsingStore() },
      bubbles: true
    }));

    return Promise.resolve(attributeSync).catch(function (error) {
      console.warn('StorePicker: could not clear previous fulfilment cart attributes:', error);
      throw error;
    });
  }

  function parseReservationDate(value) {
    var match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return null;
    var date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  function compactReservationTime(value) {
    return String(value || '')
      .replace(/\s*\([^)]*\)\s*/g, ' ')
      .replace(/:00(?=\s*[ap]m)/gi, '')
      .replace(/\s*-\s*/g, '\u2013')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  function headerSlotLabel() {
    if (!reservationIsLive()) return slotExpiredPrompt ? 'Choose a new time' : 'When';

    var date = parseReservationDate(getCookie('delivery_date'));
    var time = compactReservationTime(getCookie('delivery_time_short'));
    var dayLabel = '';

    if (date) {
      var today = new Date();
      today = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      var difference = Math.round((date.getTime() - today.getTime()) / 86400000);
      if (difference === 0) dayLabel = 'Today';
      else if (difference === 1) dayLabel = 'Tomorrow';
      else {
        dayLabel = new Intl.DateTimeFormat('en-AU', {
          weekday: 'short',
          day: 'numeric',
          month: 'short'
        }).format(date);
      }
    }

    return [dayLabel, time].filter(Boolean).join(', ') || 'When';
  }

  /**
   * Keep both sides of the split header control in sync with the browsing
   * store and the real reservation cookies. "When" is deliberately shown
   * until a live slot has been reserved; browse-without-slot intent is not a
   * reservation and must not be presented as one.
   */
  function updateHeaderPickingStoreLabel(storeNumber) {
    var explicitStore = hasExplicitPickingStore();
    var store = storeNumber || currentBrowsingStore();
    var storeLabel = explicitStore && store ? storeDisplayName(store) : 'Select local store';
    var slotLabel = headerSlotLabel();
    var countdownLabel = headerReservationCountdown();

    document.querySelectorAll('[data-header-store-label]').forEach(function (label) {
      label.textContent = storeLabel;
    });
    document.querySelectorAll('[data-header-slot-label]').forEach(function (label) {
      label.textContent = slotLabel;
    });
    document.querySelectorAll('[data-header-slot-countdown]').forEach(function (label) {
      label.textContent = countdownLabel;
      label.hidden = !countdownLabel;
    });
    document.querySelectorAll('[data-header-store-open]').forEach(function (button) {
      button.setAttribute('aria-label', explicitStore
        ? ('Change shopping location from ' + storeLabel)
        : 'Choose your shopping location');
    });
    document.querySelectorAll('[data-header-slot-open]').forEach(function (button) {
      var ariaLabel = slotLabel === 'When'
        ? 'Choose a collection or delivery time'
        : (slotLabel === 'Choose a new time'
          ? 'Your reserved time has expired. Choose a new collection or delivery time'
          : ('Change reserved time from ' + slotLabel));
      if (countdownLabel) ariaLabel += '. ' + countdownLabel;
      button.setAttribute('aria-label', ariaLabel);
    });
  }

  function currentBrowsingStore() {
    try {
      return localStorage.getItem('currentPickingStore')
        || localStorage.getItem('selectedLocation')
        || (typeof getCookie === 'function' && getCookie('picking_store'))
        || '';
    } catch (e) {
      return (typeof getCookie === 'function' && getCookie('picking_store')) || '';
    }
  }

  function currentPostcode() {
    return (typeof getCookie === 'function' && getCookie('postcode')) || '';
  }

  function currentSuburb() {
    return (typeof getCookie === 'function' && getCookie('suburb')) || '';
  }

  function clearBasketChangeNotice() {
    if (subtitleEl && basketChangeNotice) {
      var suffix = '\n\n' + basketChangeNotice;
      if (subtitleEl.textContent.slice(-suffix.length) === suffix) {
        subtitleEl.textContent = subtitleEl.textContent.slice(0, -suffix.length);
      }
    }
    basketChangeNotice = '';
  }

  function updateBasketChangeNotice(row) {
    clearBasketChangeNotice();
    if (!subtitleEl || !row || !hasExplicitPickingStore()) return;
    var previousStore = String(currentBrowsingStore() || '').trim();
    var targetStore = String(row.picking_store || '').trim();
    if (!previousStore || !targetStore || previousStore === targetStore) return;
    basketChangeNotice = 'Shopping from ' + storeDisplayName(targetStore)
      + ' will update your basket. Prices may change and unavailable items may be removed.';
    subtitleEl.textContent += '\n\n' + basketChangeNotice;
  }

  function selectRow(row) {
    selectedRow = row;
    clearError();
    updateBasketChangeNotice(row);
    // Delivery and C&C slots require a postcode as well as a store. Direct
    // local browsing only needs the physical store.
    var hasPostcode = !!(lookupPostcode || row.postcode);
    var hasSlotJourney = currentIntent === 'EX' || currentIntent === 'M';
    var purchaseChoice = requiresFulfilmentChoiceForCurrentOpen();
    if (selectSlotBtn) {
      selectSlotBtn.hidden = !(hasSlotJourney && hasPostcode);
      selectSlotBtn.textContent = purchaseChoice ? 'Select a time (optional)' : 'Continue';
      selectSlotBtn.classList.toggle('store-picker__button--confirm', !purchaseChoice);
      selectSlotBtn.classList.toggle('store-picker__button--secondary', purchaseChoice);
    }
    if (confirmBtn) {
      confirmBtn.hidden = requiresSlotForCurrentOpen();
      confirmBtn.textContent = purchaseChoice ? 'Start shopping' : (hasSlotJourney ? 'Browse' : 'Browse this store');
      confirmBtn.classList.toggle('store-picker__button--confirm', purchaseChoice || !hasSlotJourney);
      confirmBtn.classList.toggle('store-picker__button--secondary', !purchaseChoice && hasSlotJourney);
    }
    if (actionsEl) {
      actionsEl.hidden = false;
      actionsEl.classList.toggle(
        'store-picker__actions--single',
        !hasSlotJourney || requiresSlotForCurrentOpen()
      );
    }
  }

  function showSuburbPicker(rows) {
    lookupRows = rows;
    selectedRow = null;
    clearBasketChangeNotice();
    if (selectSlotBtn) selectSlotBtn.hidden = true;
    if (confirmBtn) confirmBtn.hidden = true;
    if (actionsEl) actionsEl.hidden = true;
    if (!suburbSelect || !suburbWrapper) return;

    suburbSelect.innerHTML = '<option value="">Select your suburb</option>';
    rows.forEach(function (row, index) {
      var option = document.createElement('option');
      option.value = String(index);
      option.textContent = row.suburb;
      suburbSelect.appendChild(option);
    });
    renderSuburbChoices();
    suburbWrapper.hidden = false;
  }

  function onSuburbChange() {
    if (browseSubmitPending) return;
    var index = suburbSelect.value;
    syncSuburbChoices();
    if (index === '' || !lookupRows) {
      selectedRow = null;
      clearBasketChangeNotice();
      if (selectSlotBtn) selectSlotBtn.hidden = true;
      if (confirmBtn) confirmBtn.hidden = true;
      if (actionsEl) actionsEl.hidden = true;
      return;
    }
    var row = lookupRows[parseInt(index, 10)];
    if (row) selectRow(row);
  }

  function onDirectStoreChange() {
    if (browseSubmitPending) return;
    var store = storeSelect ? String(storeSelect.value || '') : '';
    syncStoreComboboxValue();
    clearError();
    resetLookupState();
    lookupPostcode = null;

    if (!store) return;
    if (postcodeInput) postcodeInput.value = '';
    if (currentIntent === 'M') {
      var option = storeSelect.options[storeSelect.selectedIndex];
      var collectionPostcode = option ? String(option.dataset.postcode || '') : '';
      lookupPostcode = collectionPostcode;
      selectRow({
        picking_store: store,
        slot_picking_store: option ? String(option.dataset.pickingStore || store) : store,
        suburb: option ? String(option.dataset.city || '') : '',
        postcode: collectionPostcode
      });
      return;
    }
    selectRow({ picking_store: store, suburb: '', postcode: '' });
  }

  function onPostcodeInput() {
    if (browseSubmitPending) return;
    if (storeSelect && storeSelect.value) {
      storeSelect.value = '';
      syncStoreComboboxValue();
    }
    // Edits invalidate both visible choices and outstanding callbacks, even
    // when the shopper changes back to the same postcode before it responds.
    clearError();
    resetLookupState();
    lookupPostcode = null;
  }

  function setJourneyPressedState() {
    if (!intentButtons) return;
    intentButtons.forEach(function (button) {
      button.setAttribute('aria-pressed', String(button.dataset.storePickerIntent === currentIntent));
    });
  }

  function chooseJourney(intent) {
    if (browseSubmitPending) return;
    if (intent !== 'browse' && intent !== 'M' && intent !== 'EX') return;
    if ((requiresSlotForCurrentOpen() || requiresFulfilmentChoiceForCurrentOpen()) && intent === 'browse') return;
    currentIntent = intent;
    if (loginCard) loginCard.hidden = true;
    clearError();
    resetLookupState();
    lookupPostcode = null;
    setJourneyPressedState();

    if (form) form.hidden = false;
    if (storeWrapper) storeWrapper.hidden = intent === 'EX';
    if (postcodeWrapper) postcodeWrapper.hidden = intent !== 'EX';
    if (suburbWrapper) suburbWrapper.hidden = true;
    if (storeSelect) storeSelect.value = '';
    syncStoreComboboxValue();
    if (postcodeInput) postcodeInput.value = '';

    if (intent === 'browse') {
      if (subtitleEl) subtitleEl.textContent = 'Choose a Harris Farm store to browse its local range and prices.';
      if (storeLabel) storeLabel.textContent = 'Browse your local';
      populateDirectStoreSelect();
      if (storeTrigger) storeTrigger.focus();
      else if (storeSelect) storeSelect.focus();
      return;
    }

    if (intent === 'M') {
      if (subtitleEl) subtitleEl.textContent = requiresFulfilmentChoiceForCurrentOpen()
        ? 'Choose your collection store to start shopping. You can select a time at checkout.'
        : requiresSlotForCurrentOpen()
        ? 'Choose a collection store, then select a collection time.'
        : 'Choose a collection store, then select a slot or start browsing.';
      if (storeLabel) storeLabel.textContent = 'Click & Collect store';
      loadClickCollectStores().then(function () {
        if (currentIntent === 'M' && storeTrigger) storeTrigger.focus();
      }).catch(function () { /* error is shown in the form */ });
      return;
    }

    if (subtitleEl) subtitleEl.textContent = requiresFulfilmentChoiceForCurrentOpen()
      ? 'Enter your delivery postcode to start shopping. You can select a time at checkout.'
      : requiresSlotForCurrentOpen()
      ? 'Enter your postcode, then select a delivery time.'
      : 'Enter your postcode, then select a slot or start browsing.';
    if (postcodeInput) postcodeInput.focus();
  }

  function resetJourneySelection() {
    currentIntent = null;
    if (loginCard) loginCard.hidden = false;
    setJourneyPressedState();
    resetLookupState();
    lookupPostcode = null;
    if (form) form.hidden = true;
    if (storeWrapper) storeWrapper.hidden = true;
    if (postcodeWrapper) postcodeWrapper.hidden = true;
    if (suburbWrapper) suburbWrapper.hidden = true;
    if (storeSelect) storeSelect.value = '';
    syncStoreComboboxValue();
    closeStoreCombobox(false);
    if (postcodeInput) postcodeInput.value = '';
  }

  /**
   * Azure's storefront/store-delivery-details returns one object per
   * catchment/store, each carrying a suburbs[] array, rather than one row
   * per suburb (see INTERNAL-DELIVERY-API-BRIEF.md). Flatten that into the
   * same { picking_store, suburb, ... } row shape selectRow()/showSuburbPicker()
   * already understand, so the rest of the modal doesn't need to know which
   * API answered. A catchment with no suburbs[] (shouldn't happen, but the
   * contract doesn't guarantee it) becomes a single row with suburb: ''.
   */
  function normalizeAzureCatchments(catchments) {
    var rows = [];
    catchments.forEach(function (catchment) {
      var suburbs = Array.isArray(catchment.suburbs) ? catchment.suburbs : [];
      if (!suburbs.length) {
        rows.push({
          picking_store: catchment.picking_store,
          suburb: '',
          zone: catchment.zone,
          express_local_available: catchment.express_local_available
        });
        return;
      }
      suburbs.forEach(function (suburb) {
        rows.push({
          picking_store: catchment.picking_store,
          suburb: suburb,
          zone: catchment.zone,
          express_local_available: catchment.express_local_available
        });
      });
    });
    return rows;
  }

  /**
   * Resolve postcode -> picking store via the new Azure storefront API
   * (storefrontApiURL from liquid-variables.liquid). Rejects on network/CORS
   * failure, a non-OK response, or an empty catchment list - all of which the
   * caller treats as "try the legacy waterfall instead", per
   * INTERNAL-DELIVERY-API-BRIEF.md's CORS caveat (only
   * staging.harrisfarm.com.au is allowlisted on the dev API today).
   */
  function findStoreViaAzure(postcode) {
    var params = new URLSearchParams({ postcode: postcode });
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var options = {
      headers: { Accept: 'application/json' }
    };
    if (controller) options.signal = controller.signal;
    var timeoutId;
    var timeout = new Promise(function (resolve, reject) {
      timeoutId = window.setTimeout(function () {
        reject(new Error('store-delivery-details request timed out'));
        if (controller) controller.abort();
      }, POSTCODE_REQUEST_TIMEOUT_MS);
    });
    // The watchdog covers the body read too, and settles even if abort is
    // unavailable or the transport ignores it. The existing fallback can retry.
    var request = Promise.resolve().then(function () {
      return fetch(storefrontApiURL + 'storefront/store-delivery-details?' + params.toString(), options);
    }).then(function (response) {
      if (!response.ok) {
        throw new Error('store-delivery-details responded ' + response.status);
      }
      return response.json();
    }).then(function (catchments) {
      if (!Array.isArray(catchments) || !catchments.length) {
        throw new Error('store-delivery-details returned no catchments');
      }
      return normalizeAzureCatchments(catchments);
    });
    return Promise.race([request, timeout]).finally(function () {
      window.clearTimeout(timeoutId);
    });
  }

  /**
   * Azure often returns one picking_store with a long suburbs[] (e.g. 2066 →
   * Lane Cove + six suburbs). Phase 1 only needs the store to shop — don't
   * block Confirm behind a suburb pick when the store is unambiguous. Show the
   * suburb dropdown as an optional refinement (for the suburb cookie) when
   * there is more than one suburb; only *require* suburb when rows disagree on
   * picking_store.
   */
  function uniquePickingStores(rows) {
    var seen = {};
    var stores = [];
    rows.forEach(function (row) {
      var key = String(row.picking_store);
      if (!seen[key]) {
        seen[key] = true;
        stores.push(key);
      }
    });
    return stores;
  }

  function handleAzureRows(rows) {
    if (!rows.length) return;

    var stores = uniquePickingStores(rows);
    var needsSuburbToDisambiguate = stores.length > 1;
    var hasMultipleSuburbs = rows.length > 1;

    if (needsSuburbToDisambiguate) {
      showSuburbPicker(rows);
      return;
    }

    // Single store — show it immediately. If there are several suburbs in that
    // catchment, still offer the dropdown so the shopper can set suburb, but
    // don't pre-select one on their behalf: showSuburbPicker() already leaves
    // selectedRow unset and Confirm hidden until the shopper actually picks.
    if (hasMultipleSuburbs) {
      showSuburbPicker(rows);
    } else {
      selectRow(rows[0]);
    }
  }

  /**
   * Legacy fallback: the existing postcodedetails.php JSONP lookup
   * (deliveryURL from liquid-variables.liquid), same endpoint and helper
   * delivery-drawer-functions.js uses. express_delivery=true because Express
   * Local is the only delivery mode this Phase-1 modal cares about (store-only
   * decisions #3/#7 in PICKING-STORE-AND-SLOT-SPLIT-PLAN.md). Resolves to
   * `null` for "no coverage" (distinct from a thrown/rejected lookup failure)
   * so the caller can show the right message either way.
   */
  function findStoreViaLegacy(postcode) {
    var params = new URLSearchParams({ a: '1', postcode: postcode, express_delivery: 'true' });

    return fetchJSONP(deliveryURL + 'postcodedetails.php?' + params.toString(), 'c', 'GET', POSTCODE_REQUEST_TIMEOUT_MS)
      .then(function (data) {
        var response = data && data.response;

        if (!response || response === 'EMPTY') {
          return null;
        }

        // postcodedetails returns either a single row object or an array of
        // rows (one per suburb in that postcode) - normalise to an array,
        // same shape delivery-drawer-functions.js expects.
        var rows = Array.isArray(response) ? response : [response];

        if (!rows.length || rows[0] === 'EMPTY' || !rows[0] || rows[0].postcode != postcode) {
          return null;
        }

        return rows;
      });
  }

  /**
   * Mirrors delivery-drawer-functions.js's loadDeliverySlots: a falsy
   * `suburb` on the first row means the postcode resolves to exactly one
   * store with no suburb disambiguation needed; otherwise show the suburb
   * picker (rows.length is usually >1 in that case, but the suburb field -
   * not the row count - is the source of truth here).
   */
  function handleLegacyRows(rows) {
    if (!rows[0].suburb) {
      selectRow(rows[0]);
    } else {
      showSuburbPicker(rows);
    }
  }

  /**
   * Resolve postcode -> picking store. Prefers the Azure storefront API and
   * falls back to the legacy postcodedetails.php JSONP waterfall on any
   * Azure failure (network/CORS/non-OK/empty) - see file header.
   */
  function findStore(postcode) {
    // Enter can resubmit a form while its submit button is disabled.
    if (findBtn && findBtn.disabled && lookupPostcode === postcode) return;
    clearError();
    resetLookupState();
    lookupPostcode = postcode;
    var generation = postcodeRequestGeneration;
    setPostcodeLookupPending(true);

    return findStoreViaAzure(postcode)
      .then(function (rows) {
        if (!isCurrentPostcodeRequest(generation, postcode)) return;
        handleAzureRows(rows);
      })
      .catch(function (azureError) {
        if (!isCurrentPostcodeRequest(generation, postcode)) return;
        console.warn('StorePicker: Azure store-delivery-details unavailable, falling back to postcodedetails.php:', azureError);
        return findStoreViaLegacy(postcode).then(function (rows) {
          if (!isCurrentPostcodeRequest(generation, postcode)) return;
          if (!rows) {
            showError("Sorry, we don't deliver to that postcode yet.");
            return;
          }
          handleLegacyRows(rows);
        });
      })
      .catch(function (error) {
        if (!isCurrentPostcodeRequest(generation, postcode)) return;
        console.error('StorePicker: postcode lookup failed:', error);
        showError("We couldn't check that postcode. Please try again.");
      })
      .finally(function () {
        if (!isCurrentPostcodeRequest(generation, postcode)) return;
        setPostcodeLookupPending(false);
      });
  }

  function normaliseAddressPart(value) {
    return String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
  }

  /**
   * Choose the address catchment without guessing. An exact suburb match wins;
   * otherwise a postcode mapping to one unique store is safe. If the postcode
   * spans several stores and the saved suburb does not match, return null so
   * the customer chooses in StorePicker after the login refresh.
   */
  function choosePreviousOrderAddressRow(rows, suburb) {
    if (!Array.isArray(rows) || !rows.length) return null;
    var wantedSuburb = normaliseAddressPart(suburb);
    if (wantedSuburb) {
      var exact = rows.find(function (row) {
        return normaliseAddressPart(row && row.suburb) === wantedSuburb;
      });
      if (exact) return exact;
    }

    var stores = uniquePickingStores(rows);
    return stores.length === 1 ? rows[0] : null;
  }

  function resolvePreviousOrderAddress(postcode) {
    return findStoreViaAzure(postcode)
      .catch(function (azureError) {
        console.warn('StorePicker: previous-order Azure lookup unavailable, using legacy postcode lookup:', azureError);
        return findStoreViaLegacy(postcode).then(function (rows) {
          return rows || [];
        });
      });
  }

  function persistPreviousOrderAddressStore(row, postcode, addressSuburb) {
    if (!row || !looksLikeStoreNumber(row.picking_store)) return false;
    // The lookup is asynchronous. Never overwrite a store selected/seeded
    // while it was in flight.
    if (hasExplicitPickingStore()) return true;

    var store = String(row.picking_store).trim();
    if (DIRECT_STORE_EXCLUSIONS[store]) return false;
    var suburb = String(addressSuburb || row.suburb || '').trim();

    if (window.locationManager && typeof window.locationManager.persistBrowsingLocation === 'function') {
      window.locationManager.persistBrowsingLocation(store);
    } else {
      localStorage.setItem('selectedLocation', store);
      localStorage.setItem('currentPickingStore', store);
      document.cookie = 'picking_store=' + store + '; path=/; max-age=' + (365 * 24 * 60 * 60);
    }

    try {
      localStorage.setItem(CHOSEN_KEY, '1');
      sessionStorage.removeItem(SESSION_DISMISS_KEY);
    } catch (e) {
      console.warn('StorePicker: could not persist address-derived store:', e);
    }

    if (typeof setCookie === 'function') {
      setCookie('postcode', postcode, 365);
      setCookie('suburb', suburb, 365);
    }
    setSlotModeIntent(null);
    queueFavouriteStoreSync(store);
    setNoPickingStoreClass(false);
    updateHeaderPickingStoreLabel(store);

    document.dispatchEvent(new CustomEvent('locationChanged', {
      detail: {
        newLocation: store,
        locationName: storeDisplayName(store),
        autoAssignedFromPreviousOrder: true
      },
      bubbles: true
    }));
    document.dispatchEvent(new CustomEvent('hfPickingStoreChanged', {
      detail: {
        store: store,
        suburb: suburb,
        postcode: postcode,
        autoAssignedFromPreviousOrder: true
      },
      bubbles: true
    }));
    return true;
  }

  /**
   * Only runs for a fresh return from the modal's native Shopify login and
   * only when neither the device nor custom.picking_store has already chosen
   * a store. Customers without a usable prior-order address fall through to
   * the normal StorePicker selection modal.
   */
  function autoAssignStoreFromPreviousOrder() {
    var address = window.siteWideSettings && window.siteWideSettings.previousOrderAddress;
    var postcode = address && String(address.postcode || '').trim();
    var suburb = address && String(address.suburb || '').trim();
    if (!/^\d{4}$/.test(postcode)) return Promise.resolve(false);

    return resolvePreviousOrderAddress(postcode)
      .then(function (rows) {
        var row = choosePreviousOrderAddressRow(rows, suburb);
        return persistPreviousOrderAddressStore(row, postcode, suburb);
      })
      .catch(function (error) {
        console.warn('StorePicker: could not auto-assign previous-order address:', error);
        return false;
      });
  }

  /**
   * True for a non-empty value that looks like a Shopify numeric customer id
   * (window.siteWideSettings.cid renders as "" for guests - see
   * liquid-variables.liquid).
   */
  function looksLikeCustomerId(value) {
    return typeof value === 'string' && /^\d+$/.test(value.trim());
  }

  /**
   * Fire-and-forget favourite-store write for logged-in shoppers (product
   * decision #4 in PICKING-STORE-AND-SLOT-SPLIT-PLAN.md). Never blocks or
   * fails confirmStore()'s UX - a rejected/erroring write just stays the
   * device-local choice until the next successful sync.
   */
  function syncFavouriteStore(store) {
    var cid = window.siteWideSettings && window.siteWideSettings.cid;
    if (!looksLikeCustomerId(cid) || !looksLikeStoreNumber(store)) {
      return Promise.resolve(false);
    }

    var params = new URLSearchParams({ cid: cid.trim(), picking_store: store });
    return fetch(storefrontApiURL + 'storefront/customer-picking-store?' + params.toString(), {
      method: 'POST',
      keepalive: true
    })
      .then(function (response) {
        if (!response.ok) {
          console.warn('StorePicker: customer-picking-store write responded ' + response.status);
          return false;
        }
        window.siteWideSettings.favouritePickingStore = String(store).trim();
        return true;
      })
      .catch(function (error) {
        console.warn('StorePicker: customer-picking-store write failed:', error);
        return false;
      });
  }

  function clearPendingFavouriteStore() {
    try {
      localStorage.removeItem(PENDING_FAVOURITE_KEY);
    } catch (e) { /* ignore */ }
  }

  function setPendingFavouriteStore(store) {
    try {
      localStorage.setItem(PENDING_FAVOURITE_KEY, String(store));
    } catch (e) {
      console.warn('StorePicker: could not persist pending favourite store:', e);
    }
  }

  function readPendingFavouriteStore() {
    try {
      return localStorage.getItem(PENDING_FAVOURITE_KEY);
    } catch (e) {
      return null;
    }
  }

  /**
   * Keep the desired account store queued until Azure confirms the metafield
   * write. This makes the login handoff resilient to a refresh, transient API
   * failure, or the preview origin not being CORS-allowlisted yet.
   */
  function queueFavouriteStoreSync(store) {
    if (!looksLikeStoreNumber(store)) return Promise.resolve(false);
    store = String(store).trim();
    setPendingFavouriteStore(store);
    return syncFavouriteStore(store).then(function (saved) {
      if (saved) clearPendingFavouriteStore();
      return saved;
    });
  }

  /**
   * On the next signed-in page: write the device picking store to custom.picking_store.
   * Pending flag (guest Confirm before sign-in) always wins. Empty account
   * favourite + explicit device store backfills once. Never overwrites a
   * non-empty account favourite from a different device store on every load.
   */
  function syncFavouriteStoreAfterLogin() {
    var cid = window.siteWideSettings && window.siteWideSettings.cid;
    if (!looksLikeCustomerId(cid)) return;

    var favourite = window.siteWideSettings && window.siteWideSettings.favouritePickingStore;
    var pending = readPendingFavouriteStore();
    if (looksLikeStoreNumber(pending)) {
      // A no-CORS preview request may have completed server-side even though
      // the browser could not read its response. Liquid confirms that case on
      // the next page render, so clear the now-satisfied queue without sending
      // the same write forever.
      if (looksLikeStoreNumber(favourite)
        && String(favourite).trim() === String(pending).trim()) {
        clearPendingFavouriteStore();
        return;
      }
      queueFavouriteStoreSync(String(pending).trim());
      return;
    }

    if (looksLikeStoreNumber(favourite)) return;
    if (!hasExplicitPickingStore()) return;

    var deviceStore = currentBrowsingStore();
    if (!looksLikeStoreNumber(deviceStore)) return;
    queueFavouriteStoreSync(String(deviceStore).trim());
  }

  /**
   * True for a value that stringifies to a positive whole store number
   * (window.siteWideSettings.favouritePickingStore renders as a JSON number,
   * e.g. 76, or null for guests/no saved store - see liquid-variables.liquid).
   */
  function looksLikeStoreNumber(value) {
    if (value === null || value === undefined || value === '') return false;
    var str = String(value).trim();
    return /^\d+$/.test(str) && parseInt(str, 10) > 0;
  }

  /**
   * Login seed (product decision #4 in PICKING-STORE-AND-SLOT-SPLIT-PLAN.md):
   * a returning logged-in customer who already saved a favourite store
   * (custom.picking_store, read server-side into
   * window.siteWideSettings.favouritePickingStore) shouldn't have to answer
   * the StorePicker prompt again on a new device/browser. Runs once at
   * init(), strictly BEFORE the auto-open check, so a successful seed makes
   * hasExplicitPickingStore() true in time to skip the modal entirely.
   *
   * Device choice always wins: bails immediately if this browser already has
   * an explicit store (hasExplicitPickingStore() - never the silent 76
   * default every anonymous visitor gets in first-loaded-scripts.liquid).
   * Does NOT call syncFavouriteStore() - the value already came from the
   * account, there's nothing new to write back. Does NOT open the modal.
   */
  function seedFavouriteStoreFromAccount() {
    if (hasExplicitPickingStore()) return;

    var favourite = window.siteWideSettings && window.siteWideSettings.favouritePickingStore;
    if (!looksLikeStoreNumber(favourite)) return;

    var store = String(favourite).trim();

    if (window.locationManager && typeof window.locationManager.persistBrowsingLocation === 'function') {
      window.locationManager.persistBrowsingLocation(store);
    } else {
      localStorage.setItem('selectedLocation', store);
      localStorage.setItem('currentPickingStore', store);
      document.cookie = 'picking_store=' + store + '; path=/; max-age=' + (365 * 24 * 60 * 60);
    }

    try {
      // Critical: without this flag the seeded store isn't "explicit" and
      // the auto-open check below would still pop the modal.
      localStorage.setItem(CHOSEN_KEY, '1');
    } catch (e) {
      console.warn('StorePicker: could not persist hf_picking_store_chosen flag during seed:', e);
    }

    setNoPickingStoreClass(false);
    updateHeaderPickingStoreLabel(store);

    // Same "let other consumers refresh" signal confirmStore() dispatches -
    // no reload either way, just lets product-card-data.js / collection
    // filtering pick up the seeded store instead of the 76 default.
    document.dispatchEvent(new CustomEvent('locationChanged', {
      detail: { newLocation: store, locationName: storeDisplayName(store), seeded: true },
      bubbles: true
    }));
    document.dispatchEvent(new CustomEvent('hfPickingStoreChanged', {
      detail: { store: store, seeded: true },
      bubbles: true
    }));
  }

  /**
   * Confirm the selected store: persist as the BROWSING picking store only
   * (never a reservation - see file header). Mirrors LocationManager's own
   * cookie/localStorage lockstep write when persistBrowsingLocation isn't
   * available yet (e.g. this modal opens before location-manager.js finishes
   * initialising).
   */
  function confirmStore(options) {
    if (!selectedRow || !selectedRow.picking_store) return false;
    options = options || {};
    if (currentIntent === 'browse' && !options.skipFulfilmentClear
      && browseNeedsReservationResolution()) {
      console.warn('StorePicker: Browse must await shared reservation cancellation before committing');
      return false;
    }
    var store = String(selectedRow.picking_store);
    var suburb = selectedRow.suburb || '';
    var postcode = lookupPostcode || selectedRow.postcode || '';
    var slotModeIntent = currentIntent === 'EX' || currentIntent === 'M' ? currentIntent : null;

    if (window.locationManager && typeof window.locationManager.persistBrowsingLocation === 'function') {
      window.locationManager.persistBrowsingLocation(store);
    } else {
      localStorage.setItem('selectedLocation', store);
      localStorage.setItem('currentPickingStore', store);
      document.cookie = 'picking_store=' + store + '; path=/; max-age=' + (365 * 24 * 60 * 60);
    }

    var cid = window.siteWideSettings && window.siteWideSettings.cid;
    if (looksLikeCustomerId(cid)) {
      queueFavouriteStoreSync(store);
    } else {
      // Guest: remember for POST on their next signed-in page load.
      setPendingFavouriteStore(store);
    }

    try {
      localStorage.setItem(CHOSEN_KEY, '1');
      sessionStorage.removeItem(SESSION_DISMISS_KEY);
    } catch (e) {
      console.warn('StorePicker: could not persist hf_picking_store_chosen flag:', e);
    }
    // Confirming this visit counts as "already told" — don't toast afterwards.
    markWelcomeShownThisSession();
    hideWelcomeToast();

    if (typeof setCookie === 'function') {
      setCookie('postcode', postcode, 365);
      setCookie('suburb', suburb, 365);
    }
    setSlotModeIntent(slotModeIntent);
    if (currentIntent === 'browse' && !options.skipFulfilmentClear) {
      clearFulfilmentReservationForBrowse().catch(function (error) {
        console.warn('StorePicker: browse reservation clear did not complete:', error);
      });
    }

    setNoPickingStoreClass(false);
    updateHeaderPickingStoreLabel(store);

    // persistBrowsingLocation() deliberately does NOT dispatch locationChanged
    // (that event also drives the old drawer's reservation reload path) - fire
    // it ourselves so product-card-data.js / collection filtering refresh with
    // the new store's prices, per product rule #4.
    if (!options.suppressEvents) {
      document.dispatchEvent(new CustomEvent('locationChanged', {
        detail: {
          newLocation: store,
          locationName: storeDisplayName(store),
          deferNavigation: !!options.deferLocationNavigation,
          shoppingIntent: currentIntent
        },
        bubbles: true
      }));
      document.dispatchEvent(new CustomEvent('hfPickingStoreChanged', {
        detail: { store: store, suburb: suburb, postcode: postcode, shoppingIntent: currentIntent },
        bubbles: true
      }));
    }

    if (!options.keepOpen) close();
    return true;
  }

  function isBrowseCartLocationExempt(item) {
    var title = String(item && (item.title || item.product_title) || '').toLowerCase();
    var handle = String(item && item.handle || '').toLowerCase();
    return (item && item.requires_shipping === false)
      || Boolean(item && item.gift_card)
      || title.indexOf('delivery fee') !== -1
      || title.indexOf('service fee') !== -1
      || title.indexOf('gift card') !== -1
      || title.indexOf('delivery pass') !== -1
      || title.indexOf('pay-it-forward') !== -1
      || title.indexOf('pay it forward') !== -1
      || handle === 'delivery'
      || handle === 'service-fee'
      || handle.indexOf('pay-it-forward') !== -1;
  }

  /**
   * A Browse submit is only complete when reservation attributes are gone and
   * every location-priced product line belongs to the newly selected store.
   * updateCartItems() can resolve after an infrastructure abort, so verify the
   * server cart instead of treating promise resolution alone as success.
   */
  function verifyBrowseCartSwitch(store) {
    var timeoutId;
    var timeout = new Promise(function (resolve, reject) {
      timeoutId = window.setTimeout(function () {
        reject(new Error('Browse cart verification timed out'));
      }, 15000);
    });
    var read = Promise.resolve().then(function () {
      return fetch('/cart.js', {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' }
      });
    }).then(function (response) {
      if (!response.ok) throw new Error('cart verification responded ' + response.status);
      return response.json();
    });
    return Promise.race([read, timeout]).then(function (cart) {
      var attributes = cart && cart.attributes ? cart.attributes : {};
      var reservationAttributeKeys = [
        'delivery_date',
        'delivery_run',
        'courier',
        'unique_window',
        'delivery_type',
        'delivery_cc_store',
        'postcode',
        'delivery_suburb',
        'local_delivery_request'
      ];
      var staleAttribute = String(attributes.picking_store || '') !== String(store)
        || reservationAttributeKeys.some(function (key) {
        return String(attributes[key] || '').trim() !== '';
      });
      var staleLine = (cart.items || []).some(function (item) {
        var properties = item && item.properties ? item.properties : {};
        if (isBrowseCartLocationExempt(item)) {
          return Boolean(properties._location_id || properties._location_price);
        }
        return String(properties._location_id || '') !== String(store)
          || !(parseFloat(properties._location_price) > 0);
      });

      if (staleAttribute || staleLine) {
        throw new Error('cart still contains the previous fulfilment location');
      }
      return cart;
    }).finally(function () {
      window.clearTimeout(timeoutId);
    });
  }

  /**
   * Browse clears any previous time only through the shared, durable release
   * flow. Commit the new browsing store after cancellation is confirmed, then
   * reconcile every product line and verify the result.
   */
  function browseNeedsReservationResolution() {
    if (window.CartSlotOverlay
      && typeof window.CartSlotOverlay.isReservationUnsettled === 'function'
      && window.CartSlotOverlay.isReservationUnsettled()) return true;
    // Any reservation-owned state must be resolved by the shared release
    // flow before Browse can clear cookies or commit another shopping store.
    if (typeof getCookie === 'function' && [
      'delivery_expires_time', 'delivery_date', 'delivery_type', 'delivery_type_long',
      'delivery_unique_window', 'delivery_time', 'delivery_time_short',
      'delivery_cc_store', 'delivery_cc_name', 'delivery_courier', 'delivery_run',
      'delivery_type_changed', 'delivery_later'
    ].some(function (name) { return !!getCookie(name); })) return true;
    try {
      return localStorage.getItem('activeDelivery') === 'true'
        || localStorage.getItem('clickCollectSelected') === 'true';
    } catch (e) { return false; }
  }

  function submitBrowseWithoutSlot() {
    if (browseSubmitPending || !selectedRow || !selectedRow.picking_store) return;
    if (requiresFulfilmentChoiceForCurrentOpen()
      && ((currentIntent !== 'EX' && currentIntent !== 'M')
        || !/^\d{4}$/.test(String(lookupPostcode || selectedRow.postcode || '')))) {
      showError('Choose Delivery or Click & Collect and confirm your destination to start shopping.');
      return;
    }

    var targetStore = String(selectedRow.picking_store);
    var targetName = storeDisplayName(targetStore);
    var submittedIntent = currentIntent;
    var submittedPurchaseChoice = requiresFulfilmentChoiceForCurrentOpen();
    var originalConfirmLabel = confirmBtn ? confirmBtn.textContent : '';
    var needsRelease = browseNeedsReservationResolution();
    var releaseConfirmed = !needsRelease;
    var attempt = ++browseSubmitEpoch;
    var controls = [postcodeInput, storeSelect, storeTrigger, findBtn].concat(
      intentButtons ? Array.prototype.slice.call(intentButtons) : []
    ).filter(Boolean).map(function (control) {
      var previous = control.disabled;
      control.disabled = true;
      return { control: control, disabled: previous };
    });

    function finish() {
      if (attempt !== browseSubmitEpoch) return;
      browseSubmitPending = false;
      cancelBrowseRelease = null;
      controls.forEach(function (entry) { entry.control.disabled = entry.disabled; });
      if (confirmBtn) {
        confirmBtn.disabled = false;
        confirmBtn.textContent = originalConfirmLabel;
      }
      if (selectSlotBtn) selectSlotBtn.disabled = false;
    }

    browseSubmitPending = true;
    clearError();
    if (confirmBtn) {
      confirmBtn.disabled = true;
      confirmBtn.textContent = needsRelease ? 'Cancelling previous time...' : 'Updating cart...';
    }
    if (selectSlotBtn) selectSlotBtn.disabled = true;
    // Closing during cancellation abandons this UI continuation, not the shared
    // durable release. That operation still guards later reservations until it
    // has a definitive outcome; a late reply cannot change the browsing store.
    cancelBrowseRelease = function () {
      finish();
      browseSubmitEpoch += 1;
    };

    Promise.resolve().then(function () {
      if (attempt !== browseSubmitEpoch || !needsRelease) return;
      if (!window.CartSlotOverlay || typeof window.CartSlotOverlay.releaseForBrowse !== 'function') {
        throw new Error('Reservation cancellation is unavailable');
      }
      return window.CartSlotOverlay.releaseForBrowse();
    }).then(function () {
      if (attempt !== browseSubmitEpoch) return;
      cancelBrowseRelease = null;
      releaseConfirmed = true;
      if (!confirmStore({
        keepOpen: true,
        deferLocationNavigation: true,
        skipFulfilmentClear: true
      })) throw new Error('The shopping destination is unavailable');
      if (confirmBtn) confirmBtn.textContent = 'Updating cart...';
      // releaseForBrowse has already proved cancellation and cleared the old
      // slot. Do not send a second cart-keyed DELETE after committing this store.
      return clearFulfilmentReservationForBrowse({ forceCartSync: true, skipReservationRelease: true });
    }).then(function () {
      if (attempt !== browseSubmitEpoch) return;
      if (!window.agileCart || typeof window.agileCart.updateCartItems !== 'function') {
        throw new Error('cart reconciliation is unavailable');
      }
      close();
      var journeyLabel = submittedIntent === 'M'
        ? 'Click & Collect'
        : (submittedIntent === 'EX' ? 'Delivery' : 'Browse your local');
      return window.agileCart.updateCartItems({
        progressContext: {
          mode: submittedIntent,
          eyebrowLabel: 'Shopping from',
          timeLabel: targetName,
          storeName: journeyLabel,
          reservationLabel: 'Shopping location updated'
        }
      });
    }).then(function () {
      if (attempt !== browseSubmitEpoch) return;
      return verifyBrowseCartSwitch(targetStore);
    }).then(function () {
      if (attempt !== browseSubmitEpoch) return;
      setSlotModeIntent(submittedIntent === 'EX' || submittedIntent === 'M'
        ? submittedIntent
        : null);
      document.dispatchEvent(new CustomEvent('hfBrowseStoreReconciled', {
        detail: { store: targetStore, shoppingIntent: submittedIntent },
        bubbles: true
      }));
    }).catch(function (error) {
      if (attempt !== browseSubmitEpoch) return;
      console.error('StorePicker: Browse cart update failed:', error);
      if (window.agileCart && typeof window.agileCart.disableCheckout === 'function') {
        window.agileCart.disableCheckout();
      }
      if (!releaseConfirmed) {
        originalConfirmLabel = 'Retry cancellation';
        showError(error && error.userMessage
          ? error.userMessage
          : "We couldn't confirm cancellation of your previous time. Your shopping store has not changed. Retry cancellation, or close this window and try again later.");
        return;
      }
      browseSubmitPending = false;
      open(submittedPurchaseChoice ? { requireFulfilmentChoice: true, startWithFulfilmentChoices: true } : {});
      showFindView({ changing: true });
      chooseJourney(submittedIntent);
      showError("We couldn't finish updating your cart for " + targetName
        + (submittedPurchaseChoice ? '. Please confirm your destination and choose Start shopping again.' : '. Please choose Browse again.'));
    }).finally(finish);
  }

  /**
   * Reuse the shared slot selector inside this modal with an uncommitted
   * candidate store/postcode. CartSlotOverlay commits and reconciles that
   * context only after the shopper successfully reserves a time.
   */
  function openDeliverySlotPicker() {
    if (slotPickerOpenPending || !selectedRow || !selectedRow.picking_store) return;
    if (currentIntent !== 'EX' && currentIntent !== 'M') return;

    var postcode = lookupPostcode || selectedRow.postcode || '';
    if (!postcode) {
      showError(currentIntent === 'M'
        ? 'Choose a collection store to see available slots.'
        : 'Enter your postcode to see available delivery slots.');
      if (currentIntent === 'M' && storeSelect) storeSelect.focus();
      else if (postcodeInput) postcodeInput.focus();
      return;
    }

    if (!window.CartSlotOverlay || typeof window.CartSlotOverlay.open !== 'function') {
      showError(currentIntent === 'M'
        ? "We couldn't load collection times. Please try again."
        : "We couldn't load delivery times. Please try again.");
      return;
    }

    var requestedIntent = currentIntent;
    var requestedStore = String(selectedRow.picking_store);
    var originalLabel = selectSlotBtn ? selectSlotBtn.textContent : '';

    slotPickerOpenPending = true;
    clearError();
    if (selectSlotBtn) {
      selectSlotBtn.disabled = true;
      selectSlotBtn.textContent = 'Loading slots...';
    }
    if (confirmBtn) confirmBtn.disabled = true;

    // Slot exploration is transactional everywhere, including the main
    // StorePicker. Do not clear the shopper's current hold, mix a candidate
    // postcode with the live picking store, or reprice the cart merely because
    // they asked to SEE times. The candidate commits only after action=set.
    var hasCurrentReservation = reservationIsLive();
    window.CartSlotOverlay.open({
      withinStorePicker: true,
      returnToCart: openOptions.returnToCart === true,
      mode: requestedIntent,
      lockMode: true,
      preferCurrentReservation: hasCurrentReservation,
      preserveCurrentReservation: hasCurrentReservation,
      context: {
        store: requestedStore,
        postcode: String(postcode),
        suburb: selectedRow.suburb || '',
        name: storeDisplayName(requestedStore),
        shoppingIntent: requestedIntent
      },
      commitContext: function () {
        return confirmStore({
          keepOpen: true,
          deferLocationNavigation: true,
          skipFulfilmentClear: true,
          suppressEvents: true
        });
      }
    });

    slotPickerOpenPending = false;
    if (selectSlotBtn) {
      selectSlotBtn.disabled = false;
      selectSlotBtn.textContent = originalLabel || 'Continue';
    }
    if (confirmBtn) confirmBtn.disabled = false;
  }

  /**
   * "Already has a store" view: leads with the shopper's current picking
   * store (Keep / Change) instead of a blank postcode form - matches the
   * cart slot overlay's "Shopping from" card language.
   */
  function showCurrentStoreView() {
    var store = currentBrowsingStore();
    if (loginCard) loginCard.hidden = false;
    if (titleEl) titleEl.textContent = 'Your local store';
    if (subtitleEl) subtitleEl.textContent = 'Prices and stock are based on this store.';

    if (currentNameEl) currentNameEl.textContent = storeDisplayName(store);
    if (currentMetaEl) {
      var suburb = currentSuburb();
      var postcode = currentPostcode();
      var meta = [suburb, postcode].filter(Boolean).join(' ');
      currentMetaEl.textContent = meta;
      currentMetaEl.hidden = !meta;
    }

    if (currentPanel) currentPanel.hidden = false;
    if (findFlowEl) findFlowEl.hidden = true;
    if (actionsEl) actionsEl.hidden = true;
    if (keepBtn) {
      var currentMode = typeof getCookie === 'function' ? getCookie('delivery_type') : '';
      keepBtn.textContent = requiresSlotForCurrentOpen() && reservationIsLive()
        ? (currentMode === 'M' ? 'Keep current Click & Collect' : 'Keep current delivery')
        : 'Keep shopping here';
    }
    if (changeBtn) changeBtn.textContent = requiresSlotForCurrentOpen() ? 'Change fulfilment' : 'Change store';
  }

  /**
   * Find / Change store form. `options.changing` is true when the shopper
   * clicked "Change store" from the current-store view. With an existing
   * store they must pick another store or close (X keeps the current one) —
   * no "browse without a store" / clear-store exit from this path.
   */
  function showFindView(options) {
    var changing = !!(options && options.changing);
    var choosingFulfilment = openOptions.source === 'header-slot' || requiresFulfilmentChoiceForCurrentOpen();

    if (titleEl) {
      titleEl.textContent = choosingFulfilment
        ? 'Choose how to shop'
        : (changing ? 'Change your store' : 'Find your local');
    }
    if (subtitleEl) subtitleEl.textContent = requiresFulfilmentChoiceForCurrentOpen()
      ? 'Choose Delivery or Click & Collect before adding items. You can select a time at checkout.'
      : requiresSlotForCurrentOpen()
      ? (reservationIsLive()
        ? 'Choose delivery or Click & Collect. Your current time is kept until a new time is reserved.'
        : 'Choose Click & Collect or Delivery.')
      : "Choose how you'd like to shop.";

    if (currentPanel) currentPanel.hidden = true;
    if (findFlowEl) findFlowEl.hidden = false;

    clearError();
    resetJourneySelection();

    if (intentButtons) {
      intentButtons.forEach(function (button) {
        button.hidden = (requiresSlotForCurrentOpen() || requiresFulfilmentChoiceForCurrentOpen())
          && button.dataset.storePickerIntent === 'browse';
      });
    }

    // Announce the choices without making Browse appear preselected.
    if (titleEl) titleEl.focus();
  }

  function open(options) {
    if (!modal && !cacheElements()) return;
    if (modal.hidden) {
      storePickerOpener = document.activeElement && !modal.contains(document.activeElement)
        ? document.activeElement
        : null;
    }
    invalidatePostcodeRequest();
    openOptions = options || {};
    hideWelcomeToast();
    modal.hidden = false;
    document.body.classList.add('store-picker-open', 'overflow-hidden-tablet');
    clearError();

    var showingCurrent = hasExplicitPickingStore() && !openOptions.startWithFulfilmentChoices
      && !requiresFulfilmentChoiceForCurrentOpen();
    if (showingCurrent) {
      showCurrentStoreView();
    } else {
      showFindView();
    }

    // Main choices start on the heading; the current-store view keeps its
    // existing Keep action as the focus target.
    var focusTarget = showingCurrent
      ? (keepBtn || panel)
      : (titleEl || panel);
    if (typeof trapFocus === 'function') {
      trapFocus(panel, focusTarget);
      // The shared trap may replace a tabindex=-1 initial target. Keep the
      // heading focused while retaining its keyboard containment handlers.
      if (focusTarget === titleEl) titleEl.focus();
    } else if (focusTarget) {
      focusTarget.focus();
    }
  }

  function close() {
    if (!modal) return true;
    if (window.CartSlotOverlay
      && typeof window.CartSlotOverlay.isWithinStorePicker === 'function'
      && window.CartSlotOverlay.isWithinStorePicker()) {
      if (window.CartSlotOverlay.close({ restoreStorePickerFocus: false }) === false) return false;
    }
    if (cancelBrowseRelease) cancelBrowseRelease();
    invalidatePostcodeRequest();
    closeStoreCombobox(false);
    modal.hidden = true;
    document.body.classList.remove('store-picker-open', 'overflow-hidden-tablet');
    var focusTarget = storePickerOpener && storePickerOpener.isConnected ? storePickerOpener : null;
    storePickerOpener = null;
    if (typeof removeTrapFocus === 'function') removeTrapFocus(focusTarget);
    else if (focusTarget) focusTarget.focus();
    // Closing without a confirmed store just leaves the pre-store state as-is
    // (product rule #2/#5) - remember the dismissal for this tab only, so we
    // don't reopen on every single page nav in the same browsing session.
    if (!hasExplicitPickingStore()) {
      try {
        sessionStorage.setItem(SESSION_DISMISS_KEY, '1');
      } catch (e) {
        // Ignore - worst case the modal reopens on the next page.
      }
    }
    openOptions = {};
    return true;
  }

  function wasDismissedThisSession() {
    try {
      return sessionStorage.getItem(SESSION_DISMISS_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function bindEvents() {
    if (loginCard) {
      // Shopify's native account component refreshes the storefront after a
      // successful login. Mark the handoff before its shadow-DOM control runs.
      loginCard.addEventListener('click', markLoginRefreshResume, true);
    }

    document.querySelectorAll('[data-store-picker-login-trigger]').forEach(function (trigger) {
      trigger.addEventListener('click', markLoginRefreshResume, true);
    });

    modal.querySelectorAll('[data-store-picker-close]').forEach(function (el) {
      el.addEventListener('click', function (event) {
        event.preventDefault();
        close();
      });
    });

    document.addEventListener('keydown', function (event) {
      if (!modal.hidden && event.key === 'Tab' && event.shiftKey && event.target === titleEl
        && typeof getFocusableElements === 'function') {
        var focusable = getFocusableElements(panel);
        if (focusable.length) {
          event.preventDefault();
          focusable[focusable.length - 1].focus();
        }
      }
      if (event.key !== 'Escape' || modal.hidden) return;
      // The embedded slot selector owns Escape while it covers this modal;
      // its close handler reveals StorePicker and restores focus here.
      if (window.CartSlotOverlay
        && typeof window.CartSlotOverlay.isWithinStorePicker === 'function'
        && window.CartSlotOverlay.isWithinStorePicker()) return;
      close();
    });

    if (form) {
      form.addEventListener('submit', function (event) {
        event.preventDefault();
        if (currentIntent !== 'EX') return;
        var postcode = (postcodeInput && postcodeInput.value || '').trim();
        if (!/^\d{4}$/.test(postcode)) {
          showError('Please enter a valid 4-digit postcode.');
          return;
        }
        if (storeSelect) storeSelect.value = '';
        syncStoreComboboxValue();
        // Resolve the store first, then let the shopper explicitly choose
        // Select a delivery slot or Browse without a slot.
        findStore(postcode);
      });
    }

    if (intentButtons) {
      intentButtons.forEach(function (button) {
        button.addEventListener('click', function (event) {
          event.preventDefault();
          chooseJourney(button.dataset.storePickerIntent);
        });
      });
    }

    if (storeSelect) {
      storeSelect.addEventListener('change', onDirectStoreChange);
    }

    if (postcodeInput) {
      postcodeInput.addEventListener('input', onPostcodeInput);
    }

    if (suburbSelect) {
      suburbSelect.addEventListener('change', onSuburbChange);
    }

    if (confirmBtn) {
      confirmBtn.addEventListener('click', function (event) {
        event.preventDefault();
        submitBrowseWithoutSlot();
      });
    }

    if (selectSlotBtn) {
      selectSlotBtn.addEventListener('click', function (event) {
        event.preventDefault();
        openDeliverySlotPicker();
      });
    }

    if (keepBtn) {
      keepBtn.addEventListener('click', function (event) {
        event.preventDefault();
        close();
      });
    }

    if (changeBtn) {
      changeBtn.addEventListener('click', function (event) {
        event.preventDefault();
        showFindView({ changing: true });
      });
    }

    if (welcomeChangeBtn) {
      welcomeChangeBtn.addEventListener('click', function (event) {
        event.preventDefault();
        openWelcomeChangeStore();
      });
    }

    if (welcomeDismissBtn) {
      welcomeDismissBtn.addEventListener('click', function (event) {
        event.preventDefault();
        hideWelcomeToast();
      });
    }

    document.addEventListener('hfSlotReserved', function (event) {
      slotExpiredPrompt = false;
      updateHeaderPickingStoreLabel(currentBrowsingStore());
      if (!event.detail || event.detail.source !== 'store-picker') return;
      var store = currentBrowsingStore();
      if (!store) return;

      // The reservation handler closes the selector and starts its background
      // cart sync in the current task. Release the deferred collection/search
      // navigation immediately afterwards, never before Reserve time.
      setTimeout(function () {
        document.dispatchEvent(new CustomEvent('locationChanged', {
          detail: { newLocation: store, locationName: storeDisplayName(store) },
          bubbles: true
        }));
      }, 0);
    });

  }

  /** Split header: location opens StorePicker; When opens the shared slot UI. */
  function bindHeaderLocationControl() {
    document.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-header-store-open], [data-store-picker-open]');
      if (!trigger) return;
      event.preventDefault();
      event.stopPropagation();
      var cartUrl = (window.routes && window.routes.cart_url) || '/cart';
      var path = window.location.pathname || '';
      var onCartPage = path === cartUrl || /(^|\/)cart\/?$/.test(path);
      open(onCartPage ? { requireSlot: true, source: 'cart-page' } : {});
    }, true);

    document.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-header-slot-open]');
      if (!trigger) return;
      event.preventDefault();
      event.stopPropagation();

      if (!hasExplicitPickingStore()) {
        open({
          requireSlot: true,
          source: 'header-slot',
          startWithFulfilmentChoices: true
        });
        return;
      }

      var hasLiveReservation = reservationIsLive();
      var activeMode = currentSlotModeIntent();
      if (window.CartSlotOverlay && typeof window.CartSlotOverlay.open === 'function') {
        if (hasLiveReservation) {
          window.CartSlotOverlay.open({ preferCurrentReservation: true });
        } else if (activeMode) {
          window.CartSlotOverlay.open({ mode: activeMode, lockMode: true });
        } else {
          open({
            requireSlot: true,
            source: 'header-slot',
            startWithFulfilmentChoices: true
          });
        }
      } else {
        open({
          requireSlot: true,
          source: 'header-slot',
          startWithFulfilmentChoices: true
        });
      }
    }, true);
  }

  function init() {
    if (!cacheElements()) {
      console.warn('StorePicker: #store-picker-modal not found on this page - skipping init.');
      return;
    }
    enhanceCustomSelectors();
    bindEvents();
    bindHeaderLocationControl();
    populateDirectStoreSelect();

    var returningFromStorePickerLogin = hasFreshLoginRefreshResume();

    // Must run before the auto-open check below - a successful seed flips
    // hasExplicitPickingStore() to true so the modal doesn't open at all.
    seedFavouriteStoreFromAccount();
    // Guest Confirm → next signed-in page (or empty metafield + device store).
    syncFavouriteStoreAfterLogin();

    setNoPickingStoreClass(!hasExplicitPickingStore());
    updateHeaderPickingStoreLabel(currentBrowsingStore());
    if (headerCountdownTimer) clearInterval(headerCountdownTimer);
    headerCountdownTimer = setInterval(function () {
      updateHeaderPickingStoreLabel(currentBrowsingStore());
    }, 30000);

    // Keep the body class (and therefore card price placeholders) in sync
    // whenever the store changes from ANY path, not just this modal - e.g. a
    // live reservation being set via the old drawer also counts as explicit.
    document.addEventListener('locationChanged', function (event) {
      setNoPickingStoreClass(!hasExplicitPickingStore());
      var store = (event && event.detail && event.detail.newLocation) || currentBrowsingStore();
      updateHeaderPickingStoreLabel(store);
    });
    document.addEventListener('hfPickingStoreChanged', function (event) {
      if (event && event.detail && event.detail.store) {
        updateHeaderPickingStoreLabel(event.detail.store);
      }
    });
    document.addEventListener('deliveryReservationExpired', function (event) {
      if (!event.detail || event.detail.reason !== 'browse-local') {
        slotExpiredPrompt = true;
      }
      setNoPickingStoreClass(!hasExplicitPickingStore());
      updateHeaderPickingStoreLabel(currentBrowsingStore());
    });

    if (returningFromStorePickerLogin) {
      clearLoginRefreshResume();

      // An active device choice, a guest choice carried through login, or an
      // existing custom.picking_store seeded above always wins over address
      // inference. Nothing further needs choosing in that case.
      if (hasExplicitPickingStore()) {
        maybeShowWelcomeBackToast();
        return;
      }

      autoAssignStoreFromPreviousOrder().then(function (assigned) {
        setNoPickingStoreClass(!hasExplicitPickingStore());
        updateHeaderPickingStoreLabel(currentBrowsingStore());
        if (assigned) {
          maybeShowWelcomeBackToast();
        } else {
          // No order/address, no catchment, or an ambiguous postcode: reopen
          // only the StorePicker so the signed-in customer can choose.
          open();
        }
      });
      return;
    }

    if (!hasExplicitPickingStore() && !wasDismissedThisSession()) {
      open();
    } else if (hasExplicitPickingStore()) {
      // Returning visitor with a remembered store — soft remind once/session.
      maybeShowWelcomeBackToast();
    }
  }

  window.StorePicker = {
    open: open,
    close: close,
    refreshHeader: function () {
      updateHeaderPickingStoreLabel(currentBrowsingStore());
    },
    restoreFocus: function () {
      if (!modal || modal.hidden) return;
      var focusTarget = titleEl || panel;
      if (currentPanel && !currentPanel.hidden) {
        focusTarget = keepBtn || focusTarget;
      } else if (currentIntent) {
        if (selectSlotBtn && !selectSlotBtn.hidden) focusTarget = selectSlotBtn;
        else if (currentIntent === 'EX') focusTarget = postcodeInput || focusTarget;
        else focusTarget = storeTrigger || storeSelect || focusTarget;
      }
      if (typeof trapFocus === 'function') {
        trapFocus(panel, focusTarget);
        if (focusTarget === titleEl) titleEl.focus();
      } else if (focusTarget) focusTarget.focus();
    },
    hasExplicitPickingStore: hasExplicitPickingStore,
    hasFulfilmentChoice: hasFulfilmentChoice
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
